import React, { useState } from "react";
import api from "../../../services/api";

const SAMPLE_CSV = `Data,Medicação,Dose_mg,Peso_kg,Braco_Esq_cm,Braco_Dir_cm,Panturrilha_cm,Cintura_cm,Quadril_cm,Busto_cm
2026-07-01,Semaglutida,0.25,74.5,28.5,28.5,35.0,82.0,98.0,92.0
2026-07-08,Semaglutida,0.25,73.8,28.0,28.0,34.5,81.0,97.0,91.5
2026-07-15,Semaglutida,0.50,73.1,27.8,27.8,34.0,80.0,96.0,91.0`;

export default function HistoricalDataImportModal({ isOpen, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");

  if (!isOpen) return null;

  // Função para baixar arquivo CSV modelo
  const handleDownloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "mariam_modelo_historico.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Leitor & Parser de CSV com suporte total a Medicação e Medidas Femininas
  const parseCSVText = (text) => {
    setError("");
    setWarningMessage("");
    const lines = text
      .split(/\r\n|\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length <= 1) {
      setError("O arquivo precisa conter ao menos o cabeçalho e uma linha de dados.");
      return [];
    }

    const headers = lines[0]
      .toLowerCase()
      .split(/[,;]/)
      .map((h) => h.trim().replace(/"/g, ""));

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(/[,;]/).map((c) => c.trim().replace(/"/g, ""));
      if (cols.length === 0) continue;

      const record = {
        date: "",
        medication: "",
        dosageMg: "",
        weightKg: "",
        leftArmCm: "",
        rightArmCm: "",
        calfCm: "",
        waistCm: "",
        hipCm: "",
        chestCm: "",
      };

      headers.forEach((header, index) => {
        const val = cols[index] || "";
        if (header.includes("data") || header.includes("date")) {
          record.date = val;
        } else if (
          header.includes("medica") ||
          header.includes("remedio") ||
          header.includes("ativo")
        ) {
          record.medication = val;
        } else if (header.includes("dosa") || header.includes("mg")) {
          record.dosageMg = val.replace("mg", "").trim();
        } else if (header.includes("peso") || header.includes("weight")) {
          record.weightKg = val.replace("kg", "").trim();
        } else if (
          header.includes("braco_esq") ||
          header.includes("bracoesq") ||
          header.includes("leftarm")
        ) {
          record.leftArmCm = val.replace("cm", "").trim();
        } else if (
          header.includes("braco_dir") ||
          header.includes("bracodir") ||
          header.includes("rightarm")
        ) {
          record.rightArmCm = val.replace("cm", "").trim();
        } else if (header.includes("panturrilha") || header.includes("calf")) {
          record.calfCm = val.replace("cm", "").trim();
        } else if (header.includes("cintura") || header.includes("waist")) {
          record.waistCm = val.replace("cm", "").trim();
        } else if (header.includes("quadril") || header.includes("hip")) {
          record.hipCm = val.replace("cm", "").trim();
        } else if (header.includes("busto") || header.includes("torax") || header.includes("chest")) {
          record.chestCm = val.replace("cm", "").trim();
        }
      });

      if (
        record.date ||
        record.medication ||
        record.weightKg ||
        record.waistCm ||
        record.hipCm ||
        record.leftArmCm ||
        record.calfCm
      ) {
        rows.push(record);
      }
    }

    if (rows.length === 0) {
      setError("Não conseguimos identificar colunas de dados válidas no CSV.");
    }

    return rows;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError("");
    setWarningMessage("");

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === "string") {
        const rows = parseCSVText(content);
        setParsedRows(rows);
      }
    };
    reader.readAsText(selectedFile);
  };

  // Edição de células da tabela
  const handleCellChange = (index, field, value) => {
    setParsedRows((prevRows) => {
      const updated = [...prevRows];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  // Adicionar nova linha vazia na tabela
  const handleAddRow = () => {
    const todayStr = new Date().toISOString().split("T")[0];
    setParsedRows((prev) => [
      ...prev,
      {
        date: todayStr,
        medication: "",
        dosageMg: "",
        weightKg: "",
        leftArmCm: "",
        rightArmCm: "",
        calfCm: "",
        waistCm: "",
        hipCm: "",
        chestCm: "",
      },
    ]);
  };

  // Remover linha da tabela
  const handleRemoveRow = (index) => {
    setParsedRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadSubmit = async () => {
    if (parsedRows.length === 0) {
      setError("Adicione ao menos uma linha na tabela para importar.");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");
      setSuccessMessage("");
      setWarningMessage("");

      const res = await api.post("/import/history", {
        records: parsedRows,
      });

      const data = res?.data;
      const importedInfo = data?.imported;

      setSuccessMessage(
        `Sucesso! Importados ${importedInfo?.totalRecords || parsedRows.length} registro(s) para seu histórico.`
      );

      if (importedInfo?.createdMedications && importedInfo.createdMedications > 0) {
        setWarningMessage(
          `💡 Cadastramos ${importedInfo.createdMedications} nova(s) medicação(ões) automaticamente com o princípio ativo informado. Você pode complementar o lote/estoque quando quiser na tela de Medicações.`
        );
      }

      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 3000);
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(msg || "Não foi possível importar a planilha. Confira a formatação dos campos.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-purple-950/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="glass-card w-full max-w-4xl rounded-3xl p-5 sm:p-6 shadow-2xl border border-white bg-white/95 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-purple-100 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-purple-100 text-xl">
              📊
            </div>
            <div>
              <h3 className="text-base font-black text-[#2A1F45]">
                Importar Planilha de Histórico
              </h3>
              <p className="text-xs font-semibold text-purple-800">
                Suba ou edite seus dados diretamente na tabela abaixo
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-purple-900 font-bold text-lg p-1"
          >
            ✕
          </button>
        </div>

        {/* FEEDBACKS */}
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-3 text-xs font-semibold text-red-800 text-center">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-xs font-bold text-emerald-800 text-center">
            💜 {successMessage}
          </div>
        )}

        {warningMessage && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-900">
            {warningMessage}
          </div>
        )}

        {/* PASSO 1: DOWNLOAD DO MODELO E ARQUIVO */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-2xl bg-purple-50/70 p-3.5 border border-purple-100 flex flex-col justify-between space-y-2">
            <div>
              <span className="text-xs font-extrabold uppercase tracking-wider text-purple-900 block">
                1. Planilha Modelo
              </span>
              <p className="text-[11px] font-medium text-gray-600 mt-0.5">
                Baixe o modelo com formato para medicação, peso e medidas corporais.
              </p>
            </div>

            <button
              type="button"
              onClick={handleDownloadSample}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-white border border-purple-200 py-2 text-xs font-bold text-purple-800 hover:bg-purple-50 shadow-xs"
            >
              📥 Baixar Modelo (CSV)
            </button>
          </div>

          <div className="rounded-2xl bg-purple-50/70 p-3.5 border border-purple-100 flex flex-col justify-between space-y-2">
            <div>
              <span className="text-xs font-extrabold uppercase tracking-wider text-purple-900 block">
                2. Upload do Arquivo (.csv)
              </span>
              <p className="text-[11px] font-bold text-purple-900 truncate mt-0.5">
                {file ? file.name : "Nenhum arquivo selecionado"}
              </p>
            </div>

            <label className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#4A3B75] hover:bg-purple-900 text-white py-2 text-xs font-bold cursor-pointer shadow-xs">
              📂 Selecionar CSV
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* TABELA EDITÁVEL DE LANÇAMENTOS COM TODAS AS MEDIDAS */}
        <div className="space-y-2 pt-1">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xs font-black text-[#2A1F45]">
                Tabela de Lançamentos ({parsedRows.length})
              </h4>
              <p className="text-[10px] text-gray-500 font-medium">
                Você pode clicar em qualquer célula abaixo para editar ou corrigir os valores antes de salvar.
              </p>
            </div>

            <button
              type="button"
              onClick={handleAddRow}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-purple-100 text-purple-900 hover:bg-purple-200 text-xs font-bold transition-colors"
            >
              <span>+</span> Adicionar Linha
            </button>
          </div>

          <div className="max-h-64 overflow-y-auto overflow-x-auto rounded-2xl border border-purple-100 bg-white shadow-inner">
            <table className="w-full text-left text-xs min-w-[950px]">
              <thead className="bg-purple-50 text-purple-900 font-bold border-b border-purple-100 sticky top-0 z-10">
                <tr>
                  <th className="p-2 w-28">Data</th>
                  <th className="p-2 w-36">Medicação</th>
                  <th className="p-2 w-20">Dose (mg)</th>
                  <th className="p-2 w-20">Peso (kg)</th>
                  <th className="p-2 w-20">Braço Esq</th>
                  <th className="p-2 w-20">Braço Dir</th>
                  <th className="p-2 w-20">Panturrilha</th>
                  <th className="p-2 w-20">Cintura</th>
                  <th className="p-2 w-20">Quadril</th>
                  <th className="p-2 w-20">Busto</th>
                  <th className="p-2 w-10 text-center">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {parsedRows.map((r, idx) => (
                  <tr key={idx} className="hover:bg-purple-50/30">
                    <td className="p-1">
                      <input
                        type="date"
                        value={r.date || ""}
                        onChange={(e) => handleCellChange(idx, "date", e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-purple-50/30 px-2 py-1 text-xs font-bold text-purple-900 outline-none focus:border-purple-600 focus:bg-white"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        placeholder="Ex: Semaglutida"
                        value={r.medication || ""}
                        onChange={(e) => handleCellChange(idx, "medication", e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 outline-none focus:border-purple-600"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        placeholder="0.25"
                        value={r.dosageMg || ""}
                        onChange={(e) => handleCellChange(idx, "dosageMg", e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 outline-none focus:border-purple-600"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        placeholder="74.5"
                        value={r.weightKg || ""}
                        onChange={(e) => handleCellChange(idx, "weightKg", e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-bold text-gray-900 outline-none focus:border-purple-600"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        placeholder="28.5"
                        value={r.leftArmCm || ""}
                        onChange={(e) => handleCellChange(idx, "leftArmCm", e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 outline-none focus:border-purple-600"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        placeholder="28.5"
                        value={r.rightArmCm || ""}
                        onChange={(e) => handleCellChange(idx, "rightArmCm", e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 outline-none focus:border-purple-600"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        placeholder="35.0"
                        value={r.calfCm || ""}
                        onChange={(e) => handleCellChange(idx, "calfCm", e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 outline-none focus:border-purple-600"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        placeholder="82.0"
                        value={r.waistCm || ""}
                        onChange={(e) => handleCellChange(idx, "waistCm", e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 outline-none focus:border-purple-600"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        placeholder="98.0"
                        value={r.hipCm || ""}
                        onChange={(e) => handleCellChange(idx, "hipCm", e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 outline-none focus:border-purple-600"
                      />
                    </td>
                    <td className="p-1">
                      <input
                        type="text"
                        placeholder="92.0"
                        value={r.chestCm || ""}
                        onChange={(e) => handleCellChange(idx, "chestCm", e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-800 outline-none focus:border-purple-600"
                      />
                    </td>
                    <td className="p-1 text-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(idx)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Remover linha"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}

                {parsedRows.length === 0 && (
                  <tr>
                    <td colSpan="11" className="p-6 text-center text-xs font-medium text-gray-400">
                      Nenhum registro na tabela. Faça upload de um arquivo CSV ou clique em "+ Adicionar Linha".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* BOTOES DE AÇÃO */}
        <div className="flex gap-2 pt-3 border-t border-purple-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 rounded-2xl border border-gray-200 bg-white py-3 text-xs font-bold text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>

          <button
            type="button"
            disabled={isSubmitting || parsedRows.length === 0}
            onClick={handleUploadSubmit}
            className="flex-1 rounded-2xl bg-gradient-to-r from-[#4A3B75] to-[#6A51AD] hover:from-[#3B2D60] hover:to-[#594396] active:scale-95 text-white py-3 text-xs font-extrabold uppercase tracking-wider shadow-md shadow-purple-200 transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Importando...
              </>
            ) : (
              "Confirmar Importação"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
